import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo } from 'react';

import { queryKeys } from 'api/config';
import { Nullable } from 'shared_DEPRECATED/types';

import { ChatMember } from 'features/chat/config/types/ChatMember';
import { useChatQuery } from 'features/chat/hooks/query/useChat';

import { useChatInterlocutorContext } from './chatInterlocutor';

type ChatContextType = {
  lastReadMessageId: string;
  isChatMarkedAsUnread: boolean;
  unreadMessagesCount: number;
  setUnreadMessagesCount: (count: number) => void;
  resetLastReadMessageId: () => void;
  isLoading: boolean;
  isFetching: boolean;
  members: ChatMember[];
};

const ChatContext = createContext<ChatContextType | undefined>(undefined);

type ChatProviderPropTypes = {
  children: React.ReactNode;
};

export const ChatProvider = ({ children }: ChatProviderPropTypes) => {
  const queryClient = useQueryClient();
  const { chatId } = useChatInterlocutorContext();
  const {
    data,
    isInitialLoading: isLoading,
    isFetching,
  } = useChatQuery({
    chatId,
  });

  const setUnreadMessagesCount = useCallback(
    (diff: number) => {
      queryClient.setQueryData(
        [queryKeys.chats, chatId],
        (oldData?: {
          lastReadMessageId: string;
          unreadMessagesNumber: number;
        }) => {
          return oldData
            ? {
                ...oldData,
                unreadMessagesNumber:
                  oldData.unreadMessagesNumber + diff < 0
                    ? 0
                    : oldData.unreadMessagesNumber + diff,
              }
            : {
                lastReadMessageId: '',
                unreadMessagesNumber: diff < 0 ? 0 : diff,
              };
        }
      );
    },
    [chatId, queryClient]
  );

  const resetLastReadMessageId = useCallback(() => {
    queryClient.setQueryData(
      [queryKeys.chats, chatId],
      (oldData?: {
        lastReadMessageId: Nullable<string>;
        unreadMessagesNumber: number;
      }) => {
        return oldData
          ? {
              ...oldData,
              lastReadMessageId: null,
            }
          : {
              lastReadMessageId: null,
              unreadMessagesNumber: 0,
            };
      }
    );
  }, [chatId, queryClient]);

  const providerValue = useMemo(
    () => ({
      lastReadMessageId: data?.lastReadMessageId,
      unreadMessagesCount: data?.unreadMessagesNumber || 0,
      members: data?.members || [],
      isChatMarkedAsUnread: data?.markedAsReturnTo,
      setUnreadMessagesCount,
      resetLastReadMessageId,
      isLoading,
      isFetching,
    }),
    [
      data,
      isLoading,
      isFetching,
      setUnreadMessagesCount,
      resetLastReadMessageId,
    ]
  );

  if (isLoading) {
    return null;
  }

  return (
    <ChatContext.Provider value={providerValue}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  const chatContext = useContext(ChatContext);

  if (!chatContext) {
    throw new Error('useChatContext must be used in ChatContext.Provider');
  }

  return chatContext;
};
